// src/services/menuService.js
import apiClient from '../../../api/client';
import inventoryService from '../pages/inventory/services/inventoryService'; // ✅ FIXED: Add missing import

/**
 * Service for managing menu items
 */
const menuService = {
  /**
   * Get all menu items
   * @param {Object} filters - Optional query parameters
   * @returns {Promise<Array>} - Array of menu items
   */
  getAll: async (filters = {}) => {
    try {
      // Convert filters object to query string
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      // Use the correct endpoint from pos.route.js
      const response = await apiClient.get(`/api/v1/pos/pos-items${query}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching menu items:', error);
      throw error;
    }
  },

  getAllCategories: async () => {
    try {
      const response = await apiClient.get('/api/v1/pos/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Create a new menu item
   * @param {Object} menuItem - Menu item data
   * @returns {Promise<Object>} - Created menu item
   */
  create: async (menuItemData) => {
    try {
      const response = await apiClient.post('/api/v1/pos/pos-items', menuItemData);
      return response.data;
    } catch (error) {
      console.error('Error creating menu item:', error);
      throw error;
    }
  },

  /**
   * Get all menu items and their categories
   * @returns {Promise<Array>} - Array of menu items with categories
   */
  getAllWithCategories: async () => {
    try {
      const response = await apiClient.get('/api/v1/pos/pos-items');
      // Log the response to verify data structure
      console.log('Menu items response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching menu items with categories:', error);
      throw error;
    }
  },

  /**
   * Get a menu item by ID
   * @param {number} id - Menu item ID
   * @returns {Promise<Object>} - Menu item object
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/api/v1/pos/pos-items/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching menu item ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get menu items by category
   * @param {number} categoryId - Category ID
   * @returns {Promise<Array>} - Array of menu items
   */
  getByCategory: async (categoryId) => {
    try {
      const response = await apiClient.get(`/api/v1/pos/pos-items?categoryId=${categoryId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching menu items by category ${categoryId}:`, error);
      throw error;
    }
  },

  /**
 * Update a menu item
 * @param {number} id - Menu item ID
 * @param {Object} menuItem - Updated menu item data
 * @returns {Promise<Object>} - Updated menu item
 */
  update: async (itemId, itemData) => {
    try {
      console.log('🔄 Updating menu item:', itemId);
      console.log('📤 Sending data:', {
        ...itemData,
        variations: itemData.variations ? itemData.variations.map(v => ({
          name: v.name,
          price: v.price
          // Don't send variation_id for updates - let backend handle it
        })) : [],
        image: itemData.image ? (itemData.image.startsWith('data:') ? '[BASE64]' : itemData.image) : null
      });

      // ✅ UPDATED: Use the correct endpoint that matches your routes
      const response = await apiClient.put(`/api/v1/pos/pos-items/${itemId}`, {
        ...itemData,
        variations: itemData.variations || [] // Ensure variations is always an array
      });
      
      console.log('✅ Menu item updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating menu item:', error);
      throw error;
    }
  },
  /**
   * Delete a menu item
   * @param {number} id - Menu item ID
   * @returns {Promise<Object>} - Response data
   */
  delete: async (id) => {
    try {
      // Use the correct endpoint from pos.route.js
      const response = await apiClient.delete(`/api/v1/pos/pos-items/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting menu item ${id}:`, error);
      throw error;
    }
  },

  /**
   * Update menu item availability
   * @param {number} id - Menu item ID
   * @returns {Promise<Object>} - Updated menu item
   */
  toggleAvailability: async (id) => {
    try {
      const response = await apiClient.patch(`/api/v1/pos/pos-items/${id}/toggle-availability`);
      return response.data;
    } catch (error) {
      console.error('Error toggling availability:', error);
      throw error;
    }
  },

  /**
   * Submit a new order
   * @param {Object} orderData - Order details with items and payment info
   * @returns {Promise<Object>} - Order confirmation data
   */
  submitOrder: async (orderData) => {
    try {
      // Use the main POS endpoint for order submission
      const response = await apiClient.post('/api/v1/pos', orderData);
      return response.data;
    } catch (error) {
      console.error('Error submitting order:', error);
      throw error;
    }
  },

 /**
 * Get full order details
 * @returns {Promise<Array>} - Array of order details
 */
getFullOrderDetails: async () => {
    try {
      const response = await apiClient.get('/api/v1/orders/full');
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  },

  /**
   * Update order status
   * @param {number} orderId - Order ID
   * @param {string} status - New status ('completed', 'void', 'cancelled')
   * @returns {Promise<Object>} - Updated order data
   */
  updateOrderStatus: async (orderId, status) => {
    try {
      const response = await apiClient.patch(`/api/v1/pos/orders/${orderId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating order status for ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get receipt by order ID
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} - Receipt data
   */
  getReceiptByOrderId: async (orderId) => {
    try {
      const response = await apiClient.get(`/api/v1/pos/orders/${orderId}/receipt`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching receipt for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Submit order with stock reduction
   * @param {Object} orderData - Order details with items and payment info
   * @returns {Promise<Object>} - Order confirmation data
   */
  submitOrderWithStockReduction: async (orderData) => {
    try {
      console.log('📦 Processing order with stock reduction:', orderData);
      
      // 1. First, submit the order
      const orderResponse = await apiClient.post('/api/v1/pos', orderData);
      
      // 2. Then, reduce stock for each item
      if (orderResponse.data && orderResponse.data.order_id) {
        for (const item of orderData.items) {
          try {
            await inventoryService.recordMovement({
              item_id: item.item_id,
              movement_type: 'sale',
              quantity_change: -item.qty, // Negative for outgoing
              reference_id: orderResponse.data.order_id,
              reason: 'POS Sale'
            });
          } catch (stockError) {
            console.warn('⚠️ Stock reduction failed for item:', item.item_id, stockError);
            // Don't fail the order if stock update fails
          }
        }
      }
      
      return orderResponse.data;
    } catch (error) {
      console.error('❌ Error submitting order with stock reduction:', error);
      throw error;
    }
  },

  /**
   * Process refund with stock adjustment
   * @param {number} orderId - Order ID to refund
   * @param {Array} itemsToRefund - Items being refunded
   * @param {boolean} itemsWasted - Whether items are wasted/can't be restocked
   * @returns {Promise<Object>} - Refund confirmation
   */
  processRefund: async (orderId, itemsToRefund, itemsWasted = false) => {
    try {
      console.log('🔄 Processing refund for order:', orderId);
      
      // 1. Process the refund (you'll need to implement this endpoint)
      const refundResponse = await apiClient.post(`/api/v1/pos/orders/${orderId}/refund`, {
        items: itemsToRefund,
        itemsWasted
      });
      
      // 2. Add stock back if items not wasted
      if (!itemsWasted) {
        for (const item of itemsToRefund) {
          try {
            await inventoryService.recordMovement({
              item_id: item.item_id,
              movement_type: 'void',
              quantity_change: +item.qty, // Positive for incoming
              reference_id: orderId,
              reason: 'Order Refund - Items Restocked'
            });
          } catch (stockError) {
            console.warn('⚠️ Stock restoration failed for item:', item.item_id, stockError);
          }
        }
      } else {
        // Log as waste if items can't be restocked
        for (const item of itemsToRefund) {
          try {
            await inventoryService.recordMovement({
              item_id: item.item_id,
              movement_type: 'waste',
              quantity_change: 0, // No stock change, just log the waste
              reference_id: orderId,
              reason: 'Order Refund - Items Wasted'
            });
          } catch (stockError) {
            console.warn('⚠️ Waste logging failed for item:', item.item_id, stockError);
          }
        }
      }
      
      return refundResponse.data;
    } catch (error) {
      console.error('❌ Error processing refund:', error);
      throw error;
    }
  }
};

export default menuService;